import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom'; 
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/AuthenticatedPage.css';
import julianJohannes from '../components/julianjohannes.png';
import FileUpload from '../components/FileUpload';
import UrlUpload from '../components/UrlUpload';
import PopupWindow from '../components/PopupWindow';

function AuthenticatedPage() {
  const navigate = useNavigate(); 
  const { selectedWorkspace, setSelectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const { language } = useLanguage();
  const [firstName, setFirstName] = useState("");
  const [workspaces, setWorkspaces] = useState([]);
  const [welcomeMessage, setWelcomeMessage] = useState(language === 'en' ? "Your company didn't set a message yet." : "Ihr Unternehmen hat noch keine Nachricht festgelegt.");
  const [errorMessage, setErrorMessage] = useState(null);
  const [openRequests, setOpenRequests] = useState(0);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // State for the active tab
  const [activeTab, setActiveTab] = useState('upload');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          navigate('/not-registered');
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('firstname, system_role, onboarding_completed')
          .eq('id', user.id)
          .maybeSingle();
        
        if (profileError || !profileData) {
          navigate('/not-registered');
          return;
        }

        setFirstName(profileData.firstname || "");

        // Check onboarding status
        if (profileData.onboarding_completed === null) {
          navigate('/onboarding');
          return;
        }

        let fetchedWorkspaces = [];
        if (profileData.system_role?.startsWith("org_")) {
          const { data: allWorkspaces } = await supabase.from('clients').select('id, name');
          fetchedWorkspaces = allWorkspaces || [];
        } else {
          const { data: workspaceIds } = await supabase
            .from('user_workspace_roles')
            .select('workspace_id')
            .eq('user_id', user.id);

          if (workspaceIds?.length > 0) {
            const workspaceIdList = workspaceIds.map(w => w.workspace_id);
            const { data: workspaceNamesData } = await supabase
              .from('clients')
              .select('id, name')
              .in('id', workspaceIdList);
            fetchedWorkspaces = workspaceNamesData || [];
          }
        }

        setWorkspaces(fetchedWorkspaces);

        if (fetchedWorkspaces.length > 0) {
          const defaultWorkspace = fetchedWorkspaces.find(w => w.name === selectedWorkspace) || fetchedWorkspaces[0];
          setSelectedWorkspace(defaultWorkspace.name);
          fetchWelcomeMessage(defaultWorkspace.id);
        }
      } catch (error) {
        setErrorMessage(error.message);
      }
    };

    fetchUserData();
  }, []);

  const fetchWelcomeMessage = async () => {
    if (!selectedWorkspaceId) return; 
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('client_portal_welcome_message')
        .eq('id', selectedWorkspaceId) 
        .maybeSingle();
  
      if (error) throw new Error(error.message);
      setWelcomeMessage(data?.client_portal_welcome_message || "Your company didn't set a message yet.");
    } catch (error) {
      console.error("Error fetching company message:", error.message);
      setWelcomeMessage("Your company didn't set a message yet.");
    }
  };
  
  useEffect(() => {
    fetchWelcomeMessage();
  }, [selectedWorkspaceId]);

  useEffect(() => {
    const fetchOpenRequests = async () => {
      if (!selectedWorkspaceId) return;
  
      const { count } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .eq('workspace_id', selectedWorkspaceId)
        .eq('is_done', false)
        .in('status', ['open/open', 'in_progress/reopened']);
  
      setOpenRequests(count || 0); 
    };
  
    fetchOpenRequests();
  }, [selectedWorkspaceId]); 
  
  const handleFeedbackClick = () => {
    setIsPopupOpen(true);
  };

  const handleOpenRequestsClick = () => {
    navigate('/requests');
  };

  return (
    <div className="authenticated-container">
      <div className="auth-content wider-content">
        <div className="header-row">
          <p className="greeting">👋 <strong>{language === 'en' ? 'Hello' : 'Hallo'}</strong>, <strong>{firstName || "User"}</strong>!</p>
          <button className="feedback-button" onClick={handleFeedbackClick}>
            {language === 'en' ? 'Share Feedback' : 'Feedback teilen'}
          </button>
        </div>

        <p className="intro-text">
          {language === 'en' ? (
            <>
              We're thrilled to have you here. At Morrow Collective, we're passionate about helping the most 
              impactful companies like yours secure the funding they need to grow — without giving up equity. 
              Our portal is designed for easy collaboration, so you can focus on what matters most: building your vision.
            </>
          ) : (
            <>
              Wir freuen uns, Sie hier zu haben. Bei Morrow Collective sind wir leidenschaftlich daran interessiert, 
              den wirkungsvollsten Unternehmen wie Ihrem zu helfen, die Finanzierung zu sichern, die sie für ihr 
              Wachstum benötigen – ohne Eigenkapital abzugeben. Unser Portal ist für eine einfache Zusammenarbeit 
              konzipiert, damit Sie sich auf das Wesentliche konzentrieren können: Ihre Vision zu verwirklichen.
            </>
          )}
        </p>

        <p className="intro-text">
          {language === 'en' ? (
            <>
              Being a start-up ourselves, we are not perfect. We make mistakes. We are learning and growing. 
              Trying to put the bar a little higher every day. Your feedback is always welcome and appreciated.
            </>
          ) : (
            <>
              Als Start-up sind wir selbst nicht perfekt. Wir machen Fehler. Wir lernen und wachsen. 
              Wir versuchen, die Messlatte jeden Tag ein wenig höher zu legen. Ihr Feedback ist immer willkommen 
              und wird geschätzt.
            </>
          )}
        </p>

        <div className="closing-section">
          <img src={julianJohannes} alt="Julian & Johannes" className="founders-image" />
          <p className="closing-text">
            {language === 'en' ? (
              <>
                Thanks for working with us & all the best,<br />
                <strong>Julian & Johannes</strong>
              </>
            ) : (
              <>
                Danke für die Zusammenarbeit & alles Gute,<br />
                <strong>Julian & Johannes</strong>
              </>
            )}
          </p>
        </div>

        <div className="welcome-message-box">
          <h3>{language === 'en' ? 'Company Message' : 'Unternehmensnachricht'}</h3>
          <p>{welcomeMessage}</p>
        </div>

        <hr className="section-divider" />

        <div className="open-requests-box" onClick={handleOpenRequestsClick}>
          <p className="request-text">{language === 'en' ? 'Open Requests' : 'Offene Anfragen'}</p>
          <span className="request-count">{openRequests}</span>
        </div>

        <hr className="section-divider" />

        {/* Tabs container */}
        <div className="tabs-container">
          <div 
            className={`tab-left ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            {language === 'en' ? 'Upload Documents' : 'Dokumente hochladen'}
          </div>
          <div 
            className={`tab-right ${activeTab === 'urls' ? 'active' : ''}`}
            onClick={() => setActiveTab('urls')}
          >
            {language === 'en' ? 'Share URLs' : 'URLs teilen'}
          </div>
        </div>

        {/* Tab content */}
        {activeTab === 'upload' && <FileUpload />}
        {activeTab === 'urls' && <UrlUpload />}

        <hr className="section-divider-last" />

        <div className="contact-section">
          <div className="contact-info">
            <p className="contact-title">{language === 'en' ? 'Your Single Point of Contact' : 'Ihr Ansprechpartner'}</p>
            <a
              href="https://cal.com/johannes-caspari/morrow"
              className="contact-schedule"
              target="_blank"
              rel="noopener noreferrer"
            >
              {language === 'en' ? 'Schedule a call' : 'Termin vereinbaren'}
            </a>
          </div>
        </div>
      </div>
      <PopupWindow 
        isOpen={isPopupOpen} 
        onClose={() => setIsPopupOpen(false)} 
        selectedWorkspaceId={selectedWorkspaceId}
      />
    </div>
  );
}

export default AuthenticatedPage;
